/**
 * Device Checklist Inputs
 *
 * The Bridge serves per-device readiness as machine-readable *inputs*; the
 * frontend surfaces (device wizard + admin Fleet Board) derive the ordered
 * `ChecklistPosition[]` from these inputs via the one shared derivation in
 * `@ascleo/frontdesk-shared`. Serving inputs (not pre-derived positions) keeps
 * the derivation single-sourced without coupling this backend to a frontend
 * package.
 *
 * @since 1.0.0
 */

import { Schema as S } from "effect";

/**
 * Minimal device shape the checklist builder needs. Declared locally (rather
 * than importing `DeviceRecord`) so this contract module — re-exported through
 * the shared RPC group into the browser build — stays free of any bridge
 * backend (MongoDB) type dependency.
 *
 * @since 1.0.0
 */
export interface DeviceChecklistDevice {
  readonly deviceName?: string;
  readonly revoked: boolean;
}

/**
 * Per-device checklist inputs. Field names and semantics mirror the core of the
 * shared `PreflightChecklistInputs` (the hosted-web `gateway` block is added by
 * the web shell, not the Bridge).
 *
 * @since 1.0.0
 */
export const DeviceChecklistInputs = S.Struct({
  generalApiReachable: S.Boolean,
  telematikAvailable: S.Boolean,
  selectedCardReaderAvailable: S.Boolean,
  hasMedicalCard: S.Boolean,
  practiceName: S.optional(S.String),
  deviceName: S.optional(S.String),
  serverTokenConfigured: S.Boolean,
  insuiteRestUrl: S.optional(S.String),
  revoked: S.Boolean,
});
export type DeviceChecklistInputs = S.Schema.Type<typeof DeviceChecklistInputs>;

/**
 * Bridge readiness snapshot (the `/health/deep` signals).
 *
 * @since 1.0.0
 */
export interface BridgeReadinessSnapshot {
  readonly isConnectedToAPI: boolean;
  readonly telematikIsAvailable: boolean;
  readonly selectedCardReaderIsAvailable: boolean;
  readonly hasMedicalCard: boolean;
}

/**
 * Bridge-side pairing configuration surfaced into the checklist.
 *
 * @since 1.0.0
 */
export interface BridgePairingContext {
  readonly practiceName: string | undefined;
  readonly serverTokenConfigured: boolean;
  readonly insuiteRestUrl: string | undefined;
}

/**
 * Compose a device's registry record + the Bridge readiness snapshot + pairing
 * context into checklist inputs. Pure and total. `device` is null before the
 * device is registered; `revoked`/`deviceName` then default appropriately.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const inputs = buildDeviceChecklistInputs({
 *   device,
 *   readiness: { isConnectedToAPI: true, telematikIsAvailable: true, selectedCardReaderIsAvailable: true, hasMedicalCard: true },
 *   context: { practiceName: "Praxis Weber", serverTokenConfigured: true, insuiteRestUrl: "https://.../_rest_" },
 * })
 * ```
 */
export function buildDeviceChecklistInputs(params: {
  readonly device: DeviceChecklistDevice | null;
  readonly readiness: BridgeReadinessSnapshot;
  readonly context: BridgePairingContext;
}): DeviceChecklistInputs {
  const { device, readiness, context } = params;
  return {
    generalApiReachable: readiness.isConnectedToAPI,
    telematikAvailable: readiness.telematikIsAvailable,
    selectedCardReaderAvailable: readiness.selectedCardReaderIsAvailable,
    hasMedicalCard: readiness.hasMedicalCard,
    practiceName: context.practiceName,
    deviceName: device?.deviceName,
    serverTokenConfigured: context.serverTokenConfigured,
    insuiteRestUrl: context.insuiteRestUrl,
    revoked: device?.revoked ?? false,
  };
}
